// Утилита для работы с данными через статические JSON файлы
// Все данные сохраняются через API напрямую в файлы проекта

// Определяем URL API
export const getApiUrl = () => {
  const port = window.location.port;
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // В development режиме (React dev server на порту 3000)
  if (port === '3000' || hostname === 'localhost' || hostname === '127.0.0.1') {
    // Используем HTTP вместо HTTPS в dev режиме для избежания проблем с SSL сертификатом
    const apiUrl = 'http://localhost:5000/api';
    console.log(`🔍 getApiUrl: Development режим, используем ${apiUrl}`);
    return apiUrl;
  }
  
  // В production используем относительный путь (работаем через тот же домен)
  const apiUrl = '/api';
  console.log(`🔍 getApiUrl: Production режим, используем относительный путь ${apiUrl}`);
  return apiUrl;
};

// Сохранение файла через API (напрямую в файл проекта)
const saveToProjectFile = async (data, filename) => {
  try {
    console.log(`💾 saveToProjectFile: Сохранение ${filename}...`);
    console.log(`💾 saveToProjectFile: Данные (первые 100 символов):`, JSON.stringify(data).substring(0, 100));
    
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/${filename}`;
    console.log(`💾 saveToProjectFile: URL: ${url}`);
    
    console.log(`💾 saveToProjectFile: Отправка запроса на ${url}...`);
    console.log(`💾 saveToProjectFile: Метод: POST, Headers: Content-Type: application/json`);
    
    let response;
    try {
      response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
      console.log(`💾 saveToProjectFile: Ответ получен:`, response.status, response.statusText);
    } catch (fetchError) {
      console.error(`❌ saveToProjectFile: Ошибка fetch:`, fetchError);
      console.error(`❌ saveToProjectFile: Тип ошибки:`, fetchError.name);
      console.error(`❌ saveToProjectFile: Сообщение:`, fetchError.message);
      throw new Error(`Не удалось отправить запрос: ${fetchError.message}. Проверьте, что бэкенд запущен на порту 5000 и доступен.`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ saveToProjectFile: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ saveToProjectFile: ${filename} успешно сохранен:`, result.message);
    console.log(`✅ saveToProjectFile: Путь сохранения:`, result.path);
    return { 
      success: true, 
      message: result.message || `Файл ${filename} успешно сохранен в проект`,
      path: result.path
    };
  } catch (error) {
    console.error(`❌ saveToProjectFile: Ошибка сохранения ${filename} через API:`, error);
    return { 
      success: false, 
      message: `Не удалось сохранить ${filename}: ${error.message}. Убедитесь, что бэкенд запущен на порту 5000.` 
    };
  }
};

// Загрузка данных из файла через fetch
export const loadDataFromFile = async (filename, useCache = false) => {
  try {
    let url;
    let fetchOptions;
    
    // Для menu.json раньше разрешалось кеширование, но при обновлении данных
    // это приводило к тому, что пользователи видели устаревшее меню.
    // Поэтому всегда добавляем timestamp и запрещаем кеш браузера,
    // чтобы свежие блюда отображались сразу после выгрузки файлов.
    const timestamp = Date.now();
    url = `/${filename}?t=${timestamp}`;
    fetchOptions = {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    };
    
    console.log(`📥 loadDataFromFile: Загрузка ${filename} с URL: ${url}`);
      
    const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        console.error(`❌ loadDataFromFile: HTTP ошибка ${response.status} для ${filename}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`✅ loadDataFromFile: ${filename} загружен, элементов:`, Array.isArray(data) ? data.length : 'не массив');
      
      // НЕ сохраняем фотографии в localStorage (они слишком большие)
      // Для других файлов синхронизируем с localStorage
      if (filename !== 'theme.json' && filename !== 'photos.json' && filename !== 'menu.json') {
        const storageKey = `restaurant-${filename.replace('.json', '')}`;
        try {
        localStorage.setItem(storageKey, JSON.stringify(data));
        } catch (storageError) {
          console.warn(`⚠️ loadDataFromFile: Не удалось сохранить ${filename} в localStorage:`, storageError);
        }
      }
      
      return { success: true, data };
  } catch (error) {
    console.error(`❌ loadDataFromFile: Ошибка загрузки ${filename}:`, error);
    
    // Fallback на localStorage только для НЕ фотографий
    if (filename !== 'photos.json') {
    const stored = localStorage.getItem(`restaurant-${filename.replace('.json', '')}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
          console.log(`📥 loadDataFromFile: Использован fallback из localStorage для ${filename}`);
        return { success: true, data: parsed };
      } catch (e) {
          console.error(`❌ loadDataFromFile: Ошибка парсинга данных из localStorage для ${filename}:`, e);
        return { success: false, message: 'Ошибка парсинга данных' };
        }
      }
    }
    
    return { success: false, message: 'Файл не найден' };
  }
};

// Загрузка темы из файла
export const loadTheme = async () => {
  const result = await loadDataFromFile('theme.json');
  if (result.success) {
    const themeData = result.data;
    const currentThemeId = localStorage.getItem('restaurant-theme') || 'default';
    const newThemeId = themeData.currentTheme || 'default';
    
    // Синхронизируем с localStorage в правильном формате
    if (themeData.currentTheme) {
      localStorage.setItem('restaurant-theme', themeData.currentTheme);
    }
    if (themeData.themes) {
      localStorage.setItem('restaurant-themes', JSON.stringify(themeData.themes));
    }
    
    // Если тема изменилась, применяем её (но не перезаписываем, если уже применена)
    if (newThemeId !== currentThemeId) {
      // Тема изменилась в файле, но не применяем здесь - пусть Header.js это сделает
      // чтобы избежать конфликтов с уже примененной темой
    }
    
    return themeData;
  }
  // Возвращаем дефолтную тему
  const defaultTheme = {
    currentTheme: 'default',
    themes: {
      elegant: {
        id: 'elegant',
        name: 'Элегантная синяя',
        primaryColor: '#2C3E50',
        primaryLight: '#34495E',
        primaryDark: '#1C2833',
        secondaryColor: '#3498DB',
        secondaryLight: '#5DADE2',
        secondaryDark: '#2980B9',
        accentColor: '#E74C3C',
        backgroundColor: '#ECF0F1',
        textColor: '#2C3E50',
        textLight: '#566573'
      }
    }
  };
  // Синхронизируем дефолтную тему с localStorage
  localStorage.setItem('restaurant-theme', defaultTheme.currentTheme);
  localStorage.setItem('restaurant-themes', JSON.stringify(defaultTheme.themes));
  return defaultTheme;
};

// Загрузка меню из файла (оптимизированная версия)
// Сначала возвращает метаданные из localStorage для быстрого отображения,
// затем загружает полное меню с сервера в фоне
// Работает одинаково для мобильных и десктопных устройств
export const loadMenu = async (useCache = true) => {
  console.log('📥 loadMenu: Начало загрузки меню...');
  
  // Если включено кеширование, сначала пытаемся загрузить метаданные из localStorage
  // для мгновенного отображения на всех устройствах (мобильных и десктопных)
  if (useCache) {
  try {
    const metadata = localStorage.getItem('restaurant-menu-metadata');
    if (metadata) {
        const parsedMetadata = JSON.parse(metadata);
        if (Array.isArray(parsedMetadata) && parsedMetadata.length > 0) {
          console.log('📥 loadMenu: Найдены метаданные в localStorage, используем для быстрого отображения');
          // Возвращаем метаданные с placeholder для изображений
          const menuItemsWithPlaceholders = parsedMetadata.map(item => ({
            ...item,
            image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'
          }));
          console.log(`📥 loadMenu: Возвращаем ${menuItemsWithPlaceholders.length} позиций с метаданными`);
          
          // Загружаем полное меню в фоне и обновляем изображения
          loadMenuFromServer().then(fullMenu => {
            if (fullMenu && fullMenu.length > 0) {
              console.log('📥 loadMenu: Полное меню загружено в фоне, обновляем изображения');
              // Обновляем метаданные в localStorage
              const newMetadata = fullMenu.map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                weight: item.weight,
                description: item.description,
                category: item.category,
              }));
              localStorage.setItem('restaurant-menu-metadata', JSON.stringify(newMetadata));
              
              // Отправляем событие для обновления UI (если компонент подписан)
              window.dispatchEvent(new CustomEvent('menuUpdated', { detail: fullMenu }));
            }
          }).catch(error => {
            console.warn('⚠️ loadMenu: Ошибка загрузки полного меню в фоне:', error);
          });
          
          return menuItemsWithPlaceholders;
        }
    }
  } catch (error) {
    console.warn('⚠️ loadMenu: Ошибка загрузки метаданных из localStorage:', error);
  }
  }
  
  // Если метаданных нет или кеширование отключено, загружаем с сервера
  return await loadMenuFromServer();
};

// Вспомогательная функция для загрузки меню с сервера
// Работает одинаково для мобильных и десктопных устройств
// Использует браузерное кеширование для оптимизации повторных загрузок
const loadMenuFromServer = async () => {
  console.log('📥 loadMenuFromServer: Загрузка меню с сервера...');
  
  // Используем loadDataFromFile с кешированием для menu.json
  // Кеширование работает на всех устройствах одинаково
  const result = await loadDataFromFile('menu.json', true);
  
  if (result.success && Array.isArray(result.data)) {
    console.log(`✅ loadMenuFromServer: Получено позиций с сервера:`, result.data.length);
    
    // Проверяем, что все элементы меню имеют корректные данные
    const validMenuItems = result.data.filter(item => {
      return item && 
             item.id && 
             item.name && 
             item.price !== undefined;
    });
    
    // Проверяем изображения (для отладки)
    const itemsWithImages = validMenuItems.filter(item => item.image && item.image.startsWith('data:image'));
    console.log(`📥 loadMenuFromServer: Валидных позиций: ${validMenuItems.length}, с изображениями: ${itemsWithImages.length}`);
    
    // Обновляем метаданные в localStorage для следующей загрузки
    try {
      const menuMetadata = validMenuItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        weight: item.weight,
        description: item.description,
        category: item.category,
      }));
      localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
      console.log(`💾 loadMenuFromServer: Метаданные обновлены в localStorage (${menuMetadata.length} позиций)`);
    } catch (storageError) {
      console.warn('⚠️ loadMenuFromServer: Не удалось сохранить метаданные в localStorage:', storageError);
    }
    
    return validMenuItems;
  }
  
  console.warn('📥 loadMenuFromServer: Меню не загружено с сервера, проверяем localStorage (fallback)...');
  
  // Fallback на localStorage только для обратной совместимости
  try {
    const stored = localStorage.getItem('restaurant-menu');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        console.log('📥 loadMenuFromServer: Использован fallback из localStorage:', parsed.length);
        return parsed.filter(item => {
          return item && 
                 item.id && 
                 item.name && 
                 item.price !== undefined;
        });
      }
    }
  } catch (e) {
    console.warn('⚠️ loadMenuFromServer: Ошибка загрузки из localStorage:', e);
  }
  
  return [];
};

// Загрузка заявок из файла
export const loadReservations = async () => {
  const result = await loadDataFromFile('reservations.json');
  if (result.success && Array.isArray(result.data)) {
    return result.data;
  }
  return [];
};

// Загрузка категорий из файла
export const loadCategories = async () => {
  const result = await loadDataFromFile('categories.json');
  if (result.success && Array.isArray(result.data)) {
    // Пустой массив - это валидное состояние (пользователь удалил все категории)
    // Возвращаем массив как есть, даже если он пустой
    return result.data;
  }
  
  // Если файл не найден или данные не массив, возвращаем null
  // Это позволит вызывающему коду определить, что это первая загрузка
  // и решить, использовать ли дефолтные категории
  console.warn('📥 loadCategories: Файл не найден или данные не массив');
  return null;
};

// Сохранение темы в файл проекта
export const saveTheme = async (themeData) => {
  // Сохраняем в localStorage для быстрого доступа
  localStorage.setItem('restaurant-theme', themeData.currentTheme);
  localStorage.setItem('restaurant-themes', JSON.stringify(themeData.themes));
  
  // Сохраняем напрямую в файл проекта через API
  const result = await saveToProjectFile(themeData, 'theme.json');
  return result;
};

// Сохранение меню в файл проекта
export const saveMenu = async (menuItems) => {
  console.log('💾 saveMenu: Начало сохранения меню, позиций:', menuItems?.length || 0);
  
  if (!Array.isArray(menuItems)) {
    console.error('❌ saveMenu: menuItems не является массивом:', typeof menuItems);
    return { success: false, message: 'menuItems должен быть массивом' };
  }
  
  // Проверяем, что все элементы меню имеют изображения (для отладки)
  const itemsWithImages = menuItems.filter(item => item.image && item.image.startsWith('data:image'));
  console.log(`💾 saveMenu: Позиций с изображениями: ${itemsWithImages.length} из ${menuItems.length}`);
  
  // НЕ сохраняем в localStorage - меню с base64 изображениями слишком большие
  // и могут превысить лимит localStorage (обычно 5-10MB)
  // Вместо этого сохраняем только на сервер
  
  // Сохраняем напрямую в файл проекта через API
  console.log('💾 saveMenu: Отправка на сервер...');
  const result = await saveToProjectFile(menuItems, 'menu.json');
  
  if (result.success) {
    console.log('✅ saveMenu: Меню успешно сохранено в файл:', result.path);
    console.log(`✅ saveMenu: Всего позиций сохранено: ${menuItems.length}, с изображениями: ${itemsWithImages.length}`);
    
    // Если сохранение на сервер успешно, пытаемся сохранить только метаданные в localStorage
    // (без base64 изображений) для быстрого доступа
    // ВАЖНО: Сохраняем метаданные даже для пустого массива, чтобы система знала, что меню было сохранено
    try {
      // Сохраняем только метаданные (без base64 изображений) для кеширования
      const menuMetadata = menuItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        weight: item.weight,
        description: item.description,
        category: item.category,
        // НЕ сохраняем image - это слишком большой объем данных
      }));
      // Сохраняем метаданные даже если массив пустой (это валидное состояние)
      localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
      console.log(`💾 saveMenu: Метаданные меню сохранены в localStorage (${menuMetadata.length} позиций)`);
    } catch (storageError) {
      // Если localStorage переполнен, просто игнорируем ошибку
      // Главное - данные сохранены на сервере
      console.warn('⚠️ saveMenu: Не удалось сохранить метаданные в localStorage:', storageError);
    }
  } else {
    console.error('❌ saveMenu: Ошибка сохранения:', result.message);
  }
  
  return result;
};

// Сохранение заявок в файл проекта - только для админ-панели
export const saveReservations = async (reservations) => {
  // Сохраняем в localStorage для быстрого доступа
  localStorage.setItem('restaurant-reservations', JSON.stringify(reservations));
  
  // Сохраняем напрямую в файл проекта через API
  const result = await saveToProjectFile(reservations, 'reservations.json');
  return result;
};

// Автоматическое сохранение заявок в файл проекта
export const autoSaveReservations = async (reservations) => {
  // Сохраняем в localStorage для быстрого доступа
  localStorage.setItem('restaurant-reservations', JSON.stringify(reservations));
  
  // Сохраняем напрямую в файл проекта через API
  const result = await saveToProjectFile(reservations, 'reservations.json');
  return result;
};

// Сохранение категорий в файл проекта
export const saveCategories = async (categories) => {
  console.log(`💾 saveCategories: Сохранение категорий, количество: ${categories?.length || 0}`);
  
  // ВАЖНО: Сохраняем категории даже если массив пустой (это валидное состояние)
  // Сохраняем в localStorage для быстрого доступа
  localStorage.setItem('restaurant-categories', JSON.stringify(categories));
  console.log(`💾 saveCategories: Категории сохранены в localStorage (${categories?.length || 0} категорий)`);
  
  // Сохраняем напрямую в файл проекта через API
  const result = await saveToProjectFile(categories, 'categories.json');
  
  if (result.success) {
    console.log(`✅ saveCategories: Категории успешно сохранены в файл (${categories?.length || 0} категорий)`);
  } else {
    console.error('❌ saveCategories: Ошибка сохранения:', result.message);
  }
  
  return result;
};

// Автоматическое сохранение (алиасы для обратной совместимости)
export const autoSaveMenu = saveMenu;
export const saveMenuToFile = saveMenu;
export const saveReservationsToFile = saveReservations;

// Загрузка фотографий из файла
export const loadPhotos = async () => {
  console.log('📸 loadPhotos: Начало загрузки фотографий...');
  
  // Загружаем фотографии с сервера (не из localStorage, так как они слишком большие)
  const result = await loadDataFromFile('photos.json');
  
  if (result.success && Array.isArray(result.data)) {
    console.log('📸 loadPhotos: Получено фотографий с сервера:', result.data.length);
    
    // Фильтруем пустые фотографии или фотографии без изображения
    const validPhotos = result.data.filter(photo => {
      const isValid = photo && 
                     photo.id && // Проверяем наличие ID
                     photo.image && 
                     photo.image.trim() && 
                     photo.image.startsWith('data:image');
      
      if (!isValid) {
        console.warn('📸 loadPhotos: Пропущена невалидная фотография:', {
          hasId: !!photo?.id,
          hasImage: !!photo?.image,
          imageType: photo?.image?.substring(0, 20)
        });
      }
      
      return isValid;
    });
    
    console.log('📸 loadPhotos: Валидных фотографий:', validPhotos.length);
    
    // НЕ сохраняем в localStorage - фотографии слишком большие
    // Они всегда загружаются с сервера
    
    return validPhotos;
  }
  
  console.warn('📸 loadPhotos: Фотографии не загружены с сервера, проверяем localStorage (fallback)...');
  
  // Если файл не найден, проверяем localStorage только для обратной совместимости
  // но это только для старых данных
  try {
    const stored = localStorage.getItem('restaurant-photos');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        console.log('📸 loadPhotos: Найдено фотографий в localStorage (fallback):', parsed.length);
        
        // Фильтруем валидные фотографии
        const validPhotos = parsed.filter(photo => {
          return photo && 
                 photo.id &&
                 photo.image && 
                 photo.image.trim() && 
                 photo.image.startsWith('data:image');
        });
        
        console.log('📸 loadPhotos: Валидных фотографий из localStorage:', validPhotos.length);
        return validPhotos;
      }
    }
  } catch (error) {
    // Игнорируем ошибки localStorage
    console.warn('⚠️ loadPhotos: Ошибка загрузки фотографий из localStorage:', error);
  }
  
  console.log('📸 loadPhotos: Фотографии не найдены');
  return [];
};

// Сохранение одного фото (оптимизированная версия - отправляет только одно фото)
export const saveSinglePhoto = async (photo) => {
  try {
    if (!photo || !photo.id) {
      return { success: false, message: 'Фото должно иметь ID' };
    }

    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/photos/${photo.id}`;
    
    console.log(`💾 saveSinglePhoto: Сохранение фото ${photo.id}...`);
    console.log(`💾 saveSinglePhoto: Размер изображения: ${photo.image ? photo.image.length : 0} символов`);
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(photo),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ saveSinglePhoto: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ saveSinglePhoto: Фото ${photo.id} успешно сохранено`);
    
    // Обновляем метаданные в localStorage
    try {
      const metadata = localStorage.getItem('restaurant-photos-metadata');
      let photosMetadata = metadata ? JSON.parse(metadata) : [];
      
      // Обновляем или добавляем метаданные этого фото
      const existingIndex = photosMetadata.findIndex(p => p.id === photo.id);
      const photoMetadata = {
        id: photo.id,
        caption: photo.caption,
      };
      
      if (existingIndex >= 0) {
        photosMetadata[existingIndex] = photoMetadata;
      } else {
        photosMetadata.push(photoMetadata);
      }
      
      localStorage.setItem('restaurant-photos-metadata', JSON.stringify(photosMetadata));
    } catch (storageError) {
      console.warn('⚠️ saveSinglePhoto: Не удалось обновить метаданные в localStorage:', storageError);
    }
    
    return { 
      success: true, 
      message: result.message || `Фото ${photo.id} успешно сохранено`
    };
  } catch (error) {
    console.error(`❌ saveSinglePhoto: Ошибка сохранения фото:`, error);
    return { 
      success: false, 
      message: `Не удалось сохранить фото: ${error.message}. Убедитесь, что бэкенд запущен на порту 5000.` 
    };
  }
};

// Сохранение одной позиции меню (оптимизированная версия - отправляет только одну позицию)
export const saveSingleMenuItem = async (menuItem) => {
  let url;
  try {
    if (!menuItem || !menuItem.id) {
      return { success: false, message: 'Позиция меню должна иметь ID' };
    }

    const apiUrl = getApiUrl();
    url = `${apiUrl}/files/menu/${menuItem.id}`;
    
    console.log(`💾 saveSingleMenuItem: Сохранение позиции меню ${menuItem.id}...`);
    console.log(`💾 saveSingleMenuItem: URL: ${url}`);
    console.log(`💾 saveSingleMenuItem: Размер изображения: ${menuItem.image ? menuItem.image.length : 0} символов`);
    console.log(`💾 saveSingleMenuItem: Данные позиции:`, {
      id: menuItem.id,
      name: menuItem.name,
      price: menuItem.price,
      category: menuItem.category
    });
    
    let response;
    try {
      response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(menuItem),
    });
    } catch (fetchError) {
      // Обрабатываем сетевые ошибки (SSL, CORS, недоступность сервера)
      console.error(`❌ saveSingleMenuItem: Ошибка сети при запросе:`, fetchError);
      console.error(`❌ saveSingleMenuItem: Тип ошибки:`, fetchError.name);
      console.error(`❌ saveSingleMenuItem: Сообщение:`, fetchError.message);
      
      // Проверяем, является ли это ошибкой SSL сертификата или недоступности сервера
      if (fetchError.message && (
        fetchError.message.includes('certificate') || 
        fetchError.message.includes('SSL') ||
        fetchError.message.includes('HTTPS')
      )) {
        throw new Error(`Ошибка подключения к серверу: ${fetchError.message}. Возможно, проблема с SSL сертификатом. В dev режиме используйте HTTP (порт 5000) вместо HTTPS.`);
      }
      throw fetchError;
    }
    
    console.log(`💾 saveSingleMenuItem: Ответ получен, статус: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ saveSingleMenuItem: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ saveSingleMenuItem: Позиция меню ${menuItem.id} успешно сохранена`);
    
    // Обновляем метаданные в localStorage
    try {
      const metadata = localStorage.getItem('restaurant-menu-metadata');
      let menuMetadata = metadata ? JSON.parse(metadata) : [];
      
      // Обновляем или добавляем метаданные этой позиции
      const existingIndex = menuMetadata.findIndex(m => m.id === menuItem.id);
      const itemMetadata = {
        id: menuItem.id,
        name: menuItem.name,
        price: menuItem.price,
        weight: menuItem.weight,
        description: menuItem.description,
        category: menuItem.category,
      };
      
      if (existingIndex >= 0) {
        menuMetadata[existingIndex] = itemMetadata;
      } else {
        menuMetadata.push(itemMetadata);
      }
      
      localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
    } catch (storageError) {
      console.warn('⚠️ saveSingleMenuItem: Не удалось обновить метаданные в localStorage:', storageError);
    }
    
    return { 
      success: true, 
      message: result.message || `Позиция меню ${menuItem.id} успешно сохранена`
    };
  } catch (error) {
    console.error(`❌ saveSingleMenuItem: Ошибка сохранения позиции меню:`, error);
    console.error(`❌ saveSingleMenuItem: URL был: ${url}`);
    console.error(`❌ saveSingleMenuItem: Тип ошибки:`, error.name);
    console.error(`❌ saveSingleMenuItem: Сообщение:`, error.message);
    console.error(`❌ saveSingleMenuItem: Стек ошибки:`, error.stack);
    
    // Формируем более информативное сообщение об ошибке
    let errorMessage = error.message || 'Неизвестная ошибка';
    if (error.message && error.message.includes('Failed to fetch')) {
      errorMessage = `Не удалось подключиться к серверу. Проверьте:\n1. Запущен ли бэкенд на http://localhost:5000\n2. Запустите бэкенд командой: cd Backend && dotnet run\n3. Нет ли блокировки антивирусом или файрволом`;
    } else if (error.message && error.message.includes('certificate')) {
      errorMessage = `Проблема с SSL сертификатом. В dev режиме используйте HTTP (порт 5000) вместо HTTPS.`;
    }
    
    return { 
      success: false, 
      message: `Не удалось сохранить позицию меню: ${errorMessage}` 
    };
  }
};

// Пакетное добавление/обновление позиций меню (одним запросом)
export const saveMenuItemsBulk = async (menuItems) => {
  try {
    if (!Array.isArray(menuItems) || menuItems.length === 0) {
      return { success: false, message: 'Список позиций пустой' };
    }

    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/menu/bulk`;

    console.log(`💾 saveMenuItemsBulk: Отправка ${menuItems.length} позиций на сервер...`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(menuItems),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ saveMenuItemsBulk: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ saveMenuItemsBulk: Сервер обработал ${result.totalProcessed || menuItems.length} позиций. Добавлено: ${result.addedCount}, обновлено: ${result.updatedCount}`);

    // Обновляем метаданные в localStorage (без изображений)
    try {
      const metadata = localStorage.getItem('restaurant-menu-metadata');
      let menuMetadata = metadata ? JSON.parse(metadata) : [];
      const incomingIds = new Set(menuItems.map(item => item.id));

      // Удаляем старые записи по этим ID
      menuMetadata = menuMetadata.filter(item => !incomingIds.has(item.id));

      const newMetadata = menuItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        weight: item.weight,
        description: item.description,
        category: item.category,
      }));

      menuMetadata = [...menuMetadata, ...newMetadata];
      localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
      console.log(`💾 saveMenuItemsBulk: Метаданные обновлены (${menuMetadata.length} записей)`);
    } catch (storageError) {
      console.warn('⚠️ saveMenuItemsBulk: Не удалось обновить метаданные в localStorage:', storageError);
    }

    return {
      success: true,
      message: result.message || 'Позиции меню успешно сохранены',
      addedCount: result.addedCount,
      updatedCount: result.updatedCount,
      totalProcessed: result.totalProcessed || menuItems.length,
      skippedCount: result.skippedCount || 0
    };
  } catch (error) {
    console.error('❌ saveMenuItemsBulk: Ошибка пакетного сохранения меню:', error);
    return {
      success: false,
      message: `Не удалось сохранить позиции меню: ${error.message}`
    };
  }
};

// Удаление одной позиции меню
export const deleteSingleMenuItem = async (menuItemId) => {
  try {
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/menu/${menuItemId}`;
    
    console.log(`🗑️ deleteSingleMenuItem: Удаление позиции меню ${menuItemId}...`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ deleteSingleMenuItem: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ deleteSingleMenuItem: Позиция меню ${menuItemId} успешно удалена`);
    
    // Удаляем метаданные из localStorage
    try {
      const metadata = localStorage.getItem('restaurant-menu-metadata');
      if (metadata) {
        let menuMetadata = JSON.parse(metadata);
        menuMetadata = menuMetadata.filter(m => m.id !== menuItemId);
        localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
      }
    } catch (storageError) {
      console.warn('⚠️ deleteSingleMenuItem: Не удалось обновить метаданные в localStorage:', storageError);
    }
    
    return { 
      success: true, 
      message: result.message || `Позиция меню ${menuItemId} успешно удалена`
    };
  } catch (error) {
    console.error(`❌ deleteSingleMenuItem: Ошибка удаления позиции меню:`, error);
    return { 
      success: false, 
      message: `Не удалось удалить позицию меню: ${error.message}` 
    };
  }
};

export const deleteMenuItemsBulk = async (menuItemIds) => {
  try {
    if (!Array.isArray(menuItemIds) || menuItemIds.length === 0) {
      return { success: false, message: 'Список ID пуст' };
    }

    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/menu/bulk-delete`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(menuItemIds),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ deleteMenuItemsBulk: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ deleteMenuItemsBulk: Удалено позиций: ${result.removedCount}`);

    try {
      const metadata = localStorage.getItem('restaurant-menu-metadata');
      if (metadata) {
        let menuMetadata = JSON.parse(metadata);
        menuMetadata = menuMetadata.filter(item => !menuItemIds.includes(String(item.id)));
        localStorage.setItem('restaurant-menu-metadata', JSON.stringify(menuMetadata));
      }
    } catch (storageError) {
      console.warn('⚠️ deleteMenuItemsBulk: Не удалось обновить метаданные в localStorage:', storageError);
    }

    return {
      success: true,
      message: `Удалено позиций: ${result.removedCount}`,
      removedCount: result.removedCount,
      remaining: result.remaining,
    };
  } catch (error) {
    console.error('❌ deleteMenuItemsBulk: Ошибка пакетного удаления:', error);
    return {
      success: false,
      message: `Не удалось удалить позиции меню: ${error.message}`,
    };
  }
};

// Удаление одного фото
export const deleteSinglePhoto = async (photoId) => {
  try {
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/files/photos/${photoId}`;
    
    console.log(`🗑️ deleteSinglePhoto: Удаление фото ${photoId}...`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ deleteSinglePhoto: Ошибка HTTP ${response.status}:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ deleteSinglePhoto: Фото ${photoId} успешно удалено`);
    
    // Удаляем метаданные из localStorage
    try {
      const metadata = localStorage.getItem('restaurant-photos-metadata');
      if (metadata) {
        let photosMetadata = JSON.parse(metadata);
        photosMetadata = photosMetadata.filter(p => p.id !== photoId);
        localStorage.setItem('restaurant-photos-metadata', JSON.stringify(photosMetadata));
      }
    } catch (storageError) {
      console.warn('⚠️ deleteSinglePhoto: Не удалось обновить метаданные в localStorage:', storageError);
    }
    
    return { 
      success: true, 
      message: result.message || `Фото ${photoId} успешно удалено`
    };
  } catch (error) {
    console.error(`❌ deleteSinglePhoto: Ошибка удаления фото:`, error);
    return { 
      success: false, 
      message: `Не удалось удалить фото: ${error.message}` 
    };
  }
};

// Сохранение фотографий в файл проекта (старый метод - для обратной совместимости)
export const savePhotos = async (photos) => {
  // НЕ сохраняем в localStorage - фотографии в base64 слишком большие
  // и могут превысить лимит localStorage (обычно 5-10MB)
  // Вместо этого сохраняем только на сервер
  
  // Сохраняем напрямую в файл проекта через API
  const result = await saveToProjectFile(photos, 'photos.json');
  
  // Если сохранение на сервер успешно, пытаемся сохранить только метаданные в localStorage
  // (без самих изображений) для быстрого доступа
  if (result.success) {
    try {
      // Сохраняем только метаданные (без base64 изображений) для кеширования
      const photosMetadata = photos.map(photo => ({
        id: photo.id,
        caption: photo.caption,
        // НЕ сохраняем image - это слишком большой объем данных
      }));
      localStorage.setItem('restaurant-photos-metadata', JSON.stringify(photosMetadata));
    } catch (storageError) {
      // Если localStorage переполнен, просто игнорируем ошибку
      // Главное - данные сохранены на сервере
      console.warn('Не удалось сохранить метаданные в localStorage:', storageError);
    }
  }
  
  return result;
};

// Автоматическое сохранение фотографий (сохраняет на сервер)
// ИСПОЛЬЗУЕМ СТАРЫЙ МЕТОД ТОЛЬКО ДЛЯ МАССОВЫХ ОПЕРАЦИЙ
export const autoSavePhotos = async (photos) => {
  // Используем ту же логику, что и savePhotos
  return await savePhotos(photos);
};

export const savePhotosToFile = async (photos) => {
  // Алиас для обратной совместимости
  return await savePhotos(photos);
};

// Инициализация файлов (оставляем для обратной совместимости, но они не нужны)
export const initializeReservationsFile = async () => {
  return null;
};

export const initializeMenuFile = async () => {
  return null;
};

export const initializePhotosFile = async () => {
  return null;
};
